import { MappingLevel, MappingMatrixCell } from '../types'

type MappingCellInput = Pick<MappingMatrixCell, 'customer' | 'supplier' | 'isSelf'>

export const getMappingLevel = (cell: MappingCellInput): MappingLevel => {
  if (cell.isSelf) return 'self'
  if (cell.customer && cell.supplier) return 'complete'
  if (cell.customer) return 'customerOnly'
  if (cell.supplier) return 'supplierOnly'
  return 'missing'
}

// A pair is "done" only when both the customer and supplier links exist. `isCellIncomplete`
// ignores self cells (a company never links to itself).
export const isCellComplete = (cell: Pick<MappingMatrixCell, 'customer' | 'supplier'>): boolean =>
  cell.customer && cell.supplier

export const isCellIncomplete = (cell: MappingCellInput): boolean => !cell.isSelf && !isCellComplete(cell)

// Background colour per level - shared by the matrix table and every wizard grid so the status
// colour coding stays identical across views.
export const MAPPING_LEVEL_COLORS: Record<MappingLevel, string> = {
  complete: '#ffffff',
  customerOnly: '#fffbe6',
  supplierOnly: '#fffbe6',
  missing: '#fff1f0',
  self: '#fafafa'
}

// Language-neutral symbols: ● = link present, ○ = link missing. First position is the customer
// link, second is the supplier link - avoids the Finnish-derived "A"/"T" (asiakas/toimittaja)
// abbreviations, which don't translate.
const MAPPING_LEVEL_LABELS: Record<MappingLevel, string> = {
  complete: '●●',
  customerOnly: '●○',
  supplierOnly: '○●',
  missing: '○○',
  self: '—'
}

export const getMappingLabel = (level: MappingLevel): string => MAPPING_LEVEL_LABELS[level]

// Single source of truth for "every MappingLevel, in display order" - anything that needs to
// enumerate all levels (e.g. the legend) should use this instead of re-listing them.
export const MAPPING_LEVELS: MappingLevel[] = ['complete', 'customerOnly', 'supplierOnly', 'missing', 'self']

// Per-cell status shown in the wizard grids: a link already exists, only a suggestion exists
// (or a new link is about to be created), or nothing. Rendered as an antd <Badge status> dot.
export type CellSignal = 'linked' | 'suggestion' | 'none'

export const CELL_SIGNALS: CellSignal[] = ['linked', 'suggestion', 'none']

export const CELL_SIGNAL_STATUS: Record<CellSignal, 'success' | 'processing' | 'default'> = {
  linked: 'success', // green
  suggestion: 'processing', // blue
  none: 'default' // gray
}
